/**
 * Экран «Сотрудники и курьеры».
 *
 * Использует уже существующий API. Физического удаления нет: доступны заморозка
 * и разморозка. Сервер ограничивает выборку логиста обычными курьерами —
 * интерфейс это не подменяет, а лишь не показывает лишних элементов управления.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  assignableRoles,
  canAssignRoles,
  formatMoscowDateTime,
  ROLE_LABELS,
  USER_STATUS_LABELS,
  VEHICLE_TYPE_LABELS,
  type Role,
  type UserStatus,
} from '@fl/shared';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api-client';
import { useToast } from '../../ui/ToastProvider';
import {
  ActionMenu,
  Button,
  ConfirmDialog,
  EmptyState,
  DataSurface,
  ErrorState,
  Field,
  FilterPanel,
  LoadingState,
  Modal,
  Pagination,
  Select,
  StatusBadge,
  type StatusTone,
} from '../../ui/components';
import { OneTimeCodeModal, type OneTimeCode } from './OneTimeCodeModal';
import { SetPinModal } from './SetPinModal';
import { UserFormModal, type UserFormValues } from './UserFormModal';
import {
  ACTION_LABELS,
  type ActivationCodeResponse,
  type CreatedUserResponse,
  type HistoryEntry,
  type UserListResponse,
  type UserView,
} from './types';
import './users.css';

const PAGE_SIZE = 25;

/**
 * Порядок вкладок ролей.
 *
 * Курьеры первыми: их заводят и замораживают чаще всех остальных вместе взятых.
 * Список закреплён явно, а не собирается из `ROLE_LABELS`: порядок в интерфейсе
 * определяется частотой работы, а не порядком объявления перечисления.
 */
const ROLE_TABS: readonly Role[] = [
  'COURIER',
  'FLORIST',
  'WAREHOUSE',
  'MANAGER',
  'LOGISTICIAN',
  'SUPERVISOR',
  'ADMIN',
];

/** Кнопка называет, кого именно заводит: роль уже выбрана вкладкой. */
const ADD_LABELS: Record<Role, string> = {
  COURIER: 'Добавить курьера',
  FLORIST: 'Добавить флориста',
  WAREHOUSE: 'Добавить кладовщика',
  MANAGER: 'Добавить менеджера',
  LOGISTICIAN: 'Добавить логиста',
  ADMIN: 'Добавить администратора',
  SUPERVISOR: 'Добавить управляющего',
};

const STATUS_TONES: Record<UserStatus, StatusTone> = {
  ACTIVE: 'success',
  PENDING_ACTIVATION: 'warning',
  FROZEN: 'neutral',
};

function formatDate(value: string): string {
  return formatMoscowDateTime(value);
}

type PendingConfirm =
  | { kind: 'freeze'; user: UserView }
  | { kind: 'unfreeze'; user: UserView }
  | { kind: 'reset-pin'; user: UserView }
  | { kind: 'reissue'; user: UserView };

export function UsersScreen(): React.JSX.Element {
  const { client, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Право на выбор ролей и сам перечень назначаемых ролей берутся из общей
  // матрицы прав, а не из проверки «это администратор». Так «Управляющий»
  // получает всё, кроме ADMIN, а логист — по-прежнему только курьеров.
  const actorRoles = currentUser?.roles ?? [];
  const assignable = assignableRoles(actorRoles);
  const canAssign = canAssignRoles(actorRoles);

  /*
   * Роль — это вкладка, а не фильтр.
   *
   * Список сотрудников читают по одной роли за раз: «покажи курьеров», «покажи
   * флористов». Выпадающий фильтр со значением «Любая» превращал экран в кашу
   * из семи ролей, в которой нужную строку приходится искать глазами.
   *
   * Логист видит ровно одну вкладку — курьеров: остальные роли ему не
   * показывает и сам сервер.
   */
  const tabs: Role[] = ROLE_TABS.filter((value) => assignable.includes(value));
  const [status, setStatus] = useState<UserStatus>('ACTIVE');
  const [role, setRole] = useState<Role>('COURIER');
  const [offset, setOffset] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserView | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [oneTimeCode, setOneTimeCode] = useState<OneTimeCode | null>(null);
  const [historyFor, setHistoryFor] = useState<UserView | null>(null);
  const [pinTarget, setPinTarget] = useState<UserView | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  const listKey = ['users', status, role, offset] as const;

  const query = useQuery({
    queryKey: listKey,
    queryFn: () => {
      const params = new URLSearchParams({
        status,
        role,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      return client.get<UserListResponse>(`/api/users?${params.toString()}`);
    },
  });

  /** После любой успешной операции списки перезапрашиваются без перезагрузки страницы. */
  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const describe = (error: unknown): string =>
    error instanceof ApiError ? error.message : 'Нет связи с сервисом.';

  const createMutation = useMutation({
    mutationFn: (values: UserFormValues) =>
      client.post<CreatedUserResponse>('/api/users', {
        phone: values.phone,
        fullName: values.fullName,
        // Логисту сервер разрешает только курьеров, и интерфейс не пытается
        // спорить: он посылает роль открытой вкладки.
        roles: canAssign ? values.roles : ['COURIER'],
        ...(values.comment === '' ? {} : { comment: values.comment }),
        ...(values.roles.includes('COURIER')
          ? { defaultVehicleType: values.defaultVehicleType }
          : {}),
      }),
    onSuccess: async (result) => {
      setFormOpen(false);
      setFormError(null);
      // Код живёт только в state окна и нигде не кэшируется.
      setOneTimeCode({
        code: result.activationCode,
        expiresAt: result.expiresAt,
        personName: result.user.fullName,
        reason: 'created',
      });
      await invalidate();
    },
    onError: (error) => setFormError(describe(error)),
  });

  const updateMutation = useMutation({
    mutationFn: (values: UserFormValues) => {
      if (editing === null) {
        throw new Error('нет редактируемой записи');
      }
      return client.patch<{ user: UserView }>(`/api/users/${editing.id}`, {
        version: editing.version,
        fullName: values.fullName,
        phone: values.phone,
        comment: values.comment === '' ? null : values.comment,
        ...(canAssign ? { roles: values.roles } : {}),
        ...(values.roles.includes('COURIER')
          ? { defaultVehicleType: values.defaultVehicleType }
          : {}),
      });
    },
    onSuccess: async () => {
      setFormOpen(false);
      setEditing(null);
      setFormError(null);
      showToast('Изменения сохранены', 'success');
      await invalidate();
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409 && editing !== null) {
        // Чужие изменения не перетираются. Форма перезаряжается актуальными данными
        // вместе с новой версией записи — иначе повторное сохранение снова дало бы 409.
        try {
          const fresh = await client.get<{ user: UserView }>(`/api/users/${editing.id}`);
          setEditing(fresh.user);
          setFormError(
            'Запись изменена другим пользователем. Загружены актуальные данные — проверьте их и сохраните ещё раз.',
          );
        } catch {
          setFormError(
            'Запись изменена другим пользователем. Не удалось загрузить актуальные данные, закройте окно и откройте карточку заново.',
          );
        }
        await invalidate();
        return;
      }
      setFormError(describe(error));
    },
  });

  const actionMutation = useMutation({
    mutationFn: async (pending: PendingConfirm) => {
      const { user } = pending;
      switch (pending.kind) {
        case 'freeze':
          return { kind: pending.kind, data: await client.post(`/api/users/${user.id}/freeze`) };
        case 'unfreeze':
          return { kind: pending.kind, data: await client.post(`/api/users/${user.id}/unfreeze`) };
        case 'reset-pin':
          return {
            kind: pending.kind,
            data: await client.post<ActivationCodeResponse>(`/api/users/${user.id}/reset-pin`),
          };
        case 'reissue':
          return {
            kind: pending.kind,
            data: await client.post<ActivationCodeResponse>(
              `/api/users/${user.id}/activation-code/reissue`,
            ),
          };
      }
    },
    // Результат связывается с АРГУМЕНТОМ мутации, а не с текущим состоянием:
    // окно подтверждения могло закрыться, пока запрос выполнялся, и одноразовый код,
    // уже созданный на сервере, оказался бы потерян навсегда.
    onSuccess: async (result, variables) => {
      setConfirm(null);
      if (result === undefined) {
        return;
      }

      if (result.kind === 'reset-pin' || result.kind === 'reissue') {
        const payload = result.data as ActivationCodeResponse;
        setOneTimeCode({
          code: payload.activationCode,
          expiresAt: payload.expiresAt,
          personName: variables.user.fullName,
          reason: result.kind === 'reset-pin' ? 'pin-reset' : 'reissued',
        });
      } else {
        showToast(
          result.kind === 'freeze' ? 'Сотрудник заморожен' : 'Сотрудник разморожен',
          'success',
        );
      }

      await invalidate();
    },
    onError: (error) => {
      setConfirm(null);
      showToast(describe(error), 'error');
    },
  });

  // Прямое задание PIN администратором. Сам PIN живёт только в state окна и
  // уходит единственным телом запроса; в ответе, кэше и localStorage его нет.
  const setPinMutation = useMutation({
    mutationFn: async ({ user, pin }: { user: UserView; pin: string }) => {
      await client.post(`/api/users/${user.id}/pin`, { pin });
      return user;
    },
    onSuccess: async (user) => {
      setPinTarget(null);
      setPinError(null);
      showToast(`PIN сотрудника ${user.fullName} сохранён`, 'success');
      await invalidate();
    },
    onError: (error) => setPinError(describe(error)),
  });

  // Задать PIN может ТОЛЬКО администратор и только не администратору. Сервер это
  // тоже проверяет; здесь — чтобы не показывать заведомо запретное действие.
  const canSetPin = (user: UserView): boolean =>
    actorRoles.includes('ADMIN') && !user.roles.includes('ADMIN');

  const historyQuery = useQuery({
    queryKey: ['user-history', historyFor?.id],
    queryFn: () => client.get<{ items: HistoryEntry[] }>(`/api/users/${historyFor?.id}/history`),
    enabled: historyFor !== null,
  });

  const items = query.data?.items ?? [];

  return (
    <div className="stack">
      {/*
        Вкладки ролей и рабочая панель — одна поверхность.

        Прежде экран начинался с закреплённой карточки заголовка, которая
        при прокрутке наезжала на подписи фильтров: сам заголовок раздела
        и так стоит в шапке приложения, и второй копии ему не нужно.
      */}
      <FilterPanel
        actions={
          <Button
            variant="primary"
            data-testid="user-add"
            onClick={() => {
              setEditing(null);
              setFormError(null);
              setFormOpen(true);
            }}
          >
            {ADD_LABELS[role] ?? 'Добавить'}
          </Button>
        }
      >
        <div className="users__tabs" role="tablist" aria-label="Роли">
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={role === value}
              className={`users__tab${role === value ? ' users__tab--active' : ''}`}
              data-testid="user-role-tab"
              data-role={value}
              onClick={() => {
                setRole(value);
                setOffset(0);
              }}
            >
              {ROLE_LABELS[value]}
            </button>
          ))}
        </div>

        <Field label="Статус">
          {(fieldProps) => (
            <Select
              {...fieldProps}
              data-testid="user-status-filter"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as UserStatus);
                setOffset(0);
              }}
            >
              <option value="ACTIVE">Активные</option>
              <option value="PENDING_ACTIVATION">Ожидают активации</option>
              <option value="FROZEN">Замороженные</option>
            </Select>
          )}
        </Field>
      </FilterPanel>

      {query.isLoading && <LoadingState />}
      {query.isError && (
        <ErrorState description={describe(query.error)} onRetry={() => void query.refetch()} />
      )}

      {query.isSuccess && items.length === 0 && (
        <EmptyState
          title="Никого не найдено"
          description="Измените фильтры или добавьте сотрудника."
        />
      )}

      {query.isSuccess && items.length > 0 && (
        <>
          <DataSurface>
            <table className="table">
              <thead>
                <tr>
                  <th>ФИО</th>
                  <th>Телефон</th>
                  {/*
                   * Колонки «Роли» и «Статус» убраны из шапки намеренно.
                   *
                   * Роль уже выбрана вкладкой выше — колонка повторяла фильтр в
                   * каждой строке. Статус в рабочем списке всегда «Активен»:
                   * значение имеет только отличие от него, и оно показывается
                   * бейджем у имени, а не отдельной колонкой на весь список.
                   */}
                  <th>Транспорт</th>
                  <th>Изменён</th>
                  <th className="users__actions-head">Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="users__name">
                        <span className="users__name-text">{item.fullName}</span>
                        {/*
                         * Бейджами показываются только ДОПОЛНИТЕЛЬНЫЕ роли —
                         * те, что сверх выбранной вкладки. Повторять выбранную
                         * роль в каждой строке значит ничего не сообщать.
                         * Подписи берутся только для известных ролей: обращение
                         * ROLE_LABELS[неизвестная] дало бы пустой бейдж.
                         */}
                        {item.roles
                          .filter((value) => value !== role && ROLE_LABELS[value] !== undefined)
                          .map((value) => (
                            <StatusBadge key={value} tone="neutral">
                              {ROLE_LABELS[value]}
                            </StatusBadge>
                          ))}
                        {item.status !== 'ACTIVE' && (
                          <StatusBadge tone={STATUS_TONES[item.status]}>
                            {USER_STATUS_LABELS[item.status]}
                          </StatusBadge>
                        )}
                        {item.hasUnsupportedRoles && (
                          <StatusBadge tone="warning">Роли новее приложения</StatusBadge>
                        )}
                      </div>
                    </td>
                    <td className="nowrap mono">{item.phone}</td>
                    <td>
                      {/*
                       * Транспорт есть только у курьера: у флориста и логиста
                       * колонка стояла прочерком во всю высоту списка.
                       */}
                      {item.courierProfile === null
                        ? ''
                        : VEHICLE_TYPE_LABELS[item.courierProfile.defaultVehicleType]}
                    </td>
                    <td className="nowrap">{formatDate(item.updatedAt)}</td>
                    <td>
                      {/*
                       * В строке — то, что делают каждый день; остальное в «Ещё».
                       *
                       * Прежние четыре-пять кнопок сеткой растягивали строку до
                       * высоты карточки, и список переставал просматриваться
                       * взглядом. Набор действий и условия их доступности здесь
                       * не изменились — изменилось только, что видно сразу.
                       */}
                      <div className="row users__actions">
                        <Button
                          disabled={item.hasUnsupportedRoles}
                          title={
                            item.hasUnsupportedRoles
                              ? 'Роли этого пользователя заданы в более новой версии приложения'
                              : undefined
                          }
                          onClick={() => {
                            setEditing(item);
                            setFormError(null);
                            setFormOpen(true);
                          }}
                        >
                          Изменить
                        </Button>
                        {item.status === 'FROZEN' ? (
                          <Button onClick={() => setConfirm({ kind: 'unfreeze', user: item })}>
                            Разморозить
                          </Button>
                        ) : (
                          <Button onClick={() => setConfirm({ kind: 'freeze', user: item })}>
                            Заморозить
                          </Button>
                        )}
                        <ActionMenu
                          testId="user-more"
                          items={[
                            ...(canSetPin(item)
                              ? [
                                  {
                                    label: item.pinSetAt === null ? 'Задать PIN' : 'Изменить PIN',
                                    onSelect: () => {
                                      setPinError(null);
                                      setPinTarget(item);
                                    },
                                  },
                                ]
                              : []),
                            ...(item.status !== 'FROZEN'
                              ? [
                                  {
                                    label: 'Сбросить PIN',
                                    onSelect: () => setConfirm({ kind: 'reset-pin', user: item }),
                                  },
                                ]
                              : []),
                            ...(item.status === 'PENDING_ACTIVATION'
                              ? [
                                  {
                                    label: 'Новый код',
                                    onSelect: () => setConfirm({ kind: 'reissue', user: item }),
                                  },
                                ]
                              : []),
                            { label: 'История', onSelect: () => setHistoryFor(item) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataSurface>

          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={query.data.total}
            onChange={setOffset}
          />
        </>
      )}

      <UserFormModal
        open={formOpen}
        mode={editing === null ? 'create' : 'edit'}
        canAssignRoles={canAssign}
        assignable={assignable}
        initial={editing}
        defaultRole={role}
        busy={createMutation.isPending || updateMutation.isPending}
        error={formError}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
          setFormError(null);
        }}
        onSubmit={(values) => {
          if (editing === null) {
            createMutation.mutate(values);
          } else {
            updateMutation.mutate(values);
          }
        }}
      />

      <ConfirmDialog
        open={confirm !== null}
        title={
          confirm?.kind === 'freeze'
            ? 'Заморозить сотрудника?'
            : confirm?.kind === 'unfreeze'
              ? 'Разморозить сотрудника?'
              : confirm?.kind === 'reset-pin'
                ? 'Сбросить PIN?'
                : 'Перевыпустить код активации?'
        }
        description={
          confirm?.kind === 'freeze' ? (
            <>
              Вход будет закрыт немедленно, все сессии на всех устройствах — отозваны. Данные и
              история сохраняются, сотрудника можно разморозить позже.
            </>
          ) : confirm?.kind === 'unfreeze' ? (
            <>
              Сотрудник снова сможет войти. Ранее отозванные сессии не восстанавливаются —
              потребуется новый вход.
            </>
          ) : confirm?.kind === 'reset-pin' ? (
            <>
              Текущий PIN будет удалён, все сессии отозваны. Сотрудник получит одноразовый код и
              задаст новый PIN. Код показывается один раз.
            </>
          ) : (
            <>Предыдущий код перестанет действовать. Новый код показывается один раз.</>
          )
        }
        confirmLabel={
          confirm?.kind === 'freeze'
            ? 'Заморозить'
            : confirm?.kind === 'unfreeze'
              ? 'Разморозить'
              : confirm?.kind === 'reset-pin'
                ? 'Сбросить PIN'
                : 'Перевыпустить'
        }
        destructive={confirm?.kind === 'freeze' || confirm?.kind === 'reset-pin'}
        busy={actionMutation.isPending}
        onConfirm={() => {
          if (confirm !== null) {
            actionMutation.mutate(confirm);
          }
        }}
        onCancel={() => {
          // Пока операция выполняется, окно закрыть нельзя: результат может
          // содержать одноразовый код, который показывается только один раз.
          if (!actionMutation.isPending) {
            setConfirm(null);
          }
        }}
      />

      <OneTimeCodeModal value={oneTimeCode} onClose={() => setOneTimeCode(null)} />

      <SetPinModal
        open={pinTarget !== null}
        mode={pinTarget?.pinSetAt ? 'change' : 'set'}
        employeeName={pinTarget?.fullName ?? ''}
        busy={setPinMutation.isPending}
        error={pinError}
        onSubmit={(pin) => {
          if (pinTarget !== null) {
            setPinMutation.mutate({ user: pinTarget, pin });
          }
        }}
        onClose={() => {
          setPinTarget(null);
          setPinError(null);
        }}
      />

      <Modal
        open={historyFor !== null}
        title={`История: ${historyFor?.fullName ?? ''}`}
        onClose={() => setHistoryFor(null)}
      >
        {historyQuery.isLoading && <LoadingState />}
        {historyQuery.isError && <ErrorState onRetry={() => void historyQuery.refetch()} />}
        {historyQuery.data !== undefined && historyQuery.data.items.length === 0 && (
          <EmptyState title="Записей пока нет" />
        )}
        {historyQuery.data !== undefined && historyQuery.data.items.length > 0 && (
          <DataSurface>
            <table className="table">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Действие</th>
                  <th>Источник</th>
                </tr>
              </thead>
              <tbody>
                {historyQuery.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="nowrap">{formatDate(entry.occurredAt)}</td>
                    <td>{ACTION_LABELS[entry.action] ?? entry.action}</td>
                    <td>{entry.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataSurface>
        )}
      </Modal>
    </div>
  );
}
